// Award date and the IDIQ generation it implies.
//
// The generation matters because the witnessing obligation changed between
// them: Gen 2 §C.4.6 says the agency *should* witness; Gen 3 and Gen 4 require
// it. Identical plan text therefore carries a different obligation depending on
// award date. A date-range table, not an inference.

import type { SourceDocument } from '../../types';
import { lookupGeneration } from '../deterministic/generationCheck';

// The IDIQ ordering-period table lives in the deterministic layer, which owns
// the witnessing lookup. Importing it keeps one table rather than two that can
// drift apart — which is FM-04 in miniature.

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const AWARD_DATE =
  /(?:date of award|award date|awarded(?:\s+on)?|task order award|delivery order award)\D{0,40}([A-Za-z]+\s+\d{1,2},?\s+(?:19|20)\d{2}|\d{1,2}\/\d{1,2}\/(?:19|20)?\d{2}|(?:19|20)\d{2}-\d{2}-\d{2})/i;

/**
 * Look for a date stated as an award or task order date. Nothing is guessed: if
 * no award language is present the field stays empty, and the generation
 * lookup — which decides whether witnessing was "should" or "shall" — is left
 * for the user to supply rather than inferred from a document date.
 */
export function readMetadata(rawText: string): SourceDocument['metadata'] {
  const match = AWARD_DATE.exec(rawText);
  if (!match) return {};

  const raw = match[1];
  let iso: string | undefined;

  const named = /([A-Za-z]+)\s+(\d{1,2}),?\s+((?:19|20)\d{2})/.exec(raw);
  const slashed = /(\d{1,2})\/(\d{1,2})\/((?:19|20)?\d{2})/.exec(raw);
  const dashed = /((?:19|20)\d{2})-(\d{2})-(\d{2})/.exec(raw);

  if (named) {
    const month = MONTHS.indexOf(named[1].toLowerCase());
    if (month >= 0) {
      iso = `${named[3]}-${String(month + 1).padStart(2, '0')}-${named[2].padStart(2, '0')}`;
    }
  } else if (slashed) {
    const year = slashed[3].length === 2 ? `20${slashed[3]}` : slashed[3];
    iso = `${year}-${slashed[1].padStart(2, '0')}-${slashed[2].padStart(2, '0')}`;
  } else if (dashed) {
    iso = `${dashed[1]}-${dashed[2]}-${dashed[3]}`;
  }

  if (!iso) return {};
  // Where ordering periods overlap the later generation governs.
  return { awardDate: iso, idiqGeneration: lookupGeneration(iso).governing?.generation };
}
